import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useHistory } from 'react-router';

import {
    Avatar,
    Box,
    Button,
    FormControl,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    SvgIcon,
    Typography
} from '@mui/material';

import IconClose from 'app/ui-kit/icons/Close.svg';
import IconDown from 'app/ui-kit/icons/KeyboardArrowDown.svg';
import useI18n from 'hooks/use-i18n';
import useStores from 'hooks/use-stores';
import Utils from 'utils/Utils';

import useStyles from './styles';

const initialState = {
    type: '',
    order: '',
    date: '',
    bulletinFamily: '',
    score: '',
    query: ''
};

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const FAMILY_NAMES = [
    'unix',
    'blog',
    'software',
    'info',
    'exploit',
    'NVD',
    'bugbounty',
    'tools',
    'microsoft',
    'scanner',
    'ioc'
];

const Filter: React.FC = () => {
    const i18n = useI18n();
    const { classes, cx } = useStyles();
    const history = useHistory();

    const { statsStore, searchStore } = useStores();

    /** @type {[typeof initialState, (patch: Partial<typeof initialState>) => void]} */
    const [state, setState] = useReducer((state, payload) => ({ ...state, ...payload }), initialState);

    const { type, order, date, bulletinFamily, score, query } = state;

    const queryRef = useRef(query);
    queryRef.current = query;

    useEffect(() => {
        let query = Object.entries({ type, order, bulletinFamily })
            .filter(([_, value]) => Boolean(value))
            .map(([key, value]) => `${key}:${value}`)
            .join(' AND ');

        score && (query += ` cvss.score:[${score} TO 10]`);
        date && (query += ` ${date}`);

        setState({ query });
    }, [type, order, date, bulletinFamily, score]);

    const handleClear = useCallback(() => {
        setState(initialState);
        searchStore.doSearch();
    }, [searchStore]);

    const handleSearch = useCallback(() => {
        if (queryRef.current) {
            const search = `query=${queryRef.current}`;
            history.push({ pathname: '/search', search });
            searchStore.doSearch({ query });
        }
    }, [history, query, searchStore]);

    const setBulletinFamily = useCallback((event) => setState({ bulletinFamily: event.target.value }), []);
    const setType = useCallback((event) => setState({ type: event.target.value }), []);
    const setScore = useCallback((event) => setState({ score: event.target.value }), []);
    const setDate = useCallback((event) => setState({ date: event.target.value }), []);
    const setOrder = useCallback((event) => setState({ order: event.target.value }), []);

    const suggestMenu = useMemo(
        () =>
            Object.keys(statsStore.types || {})
                .sort()
                .map((key, index) => (
                    <MenuItem key={index} value={key} className={classes.suggestMenuItem}>
                        <ListItemIcon>
                            <Avatar className={classes.avatar} alt={key} src={Utils.getImage(key)} />
                        </ListItemIcon>
                        <ListItemText>{key}</ListItemText>
                    </MenuItem>
                )),
        [classes.avatar, classes.suggestMenuItem, statsStore.types]
    );

    return (
        <Box className={classes.filter}>
            <Typography variant="h5" className={classes.header}>
                {'Query Builder'}
            </Typography>

            <Box className={classes.fields}>
                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>{'Family'}</InputLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            displayEmpty
                            value={bulletinFamily}
                            IconComponent={IconDown}
                            onChange={setBulletinFamily}
                        >
                            {FAMILY_NAMES.map((name) => (
                                <MenuItem key={`family-${name}`} value={name}>
                                    <Typography className={classes.capitalize} variant="inherit">
                                        {name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>{i18n('bulletinType')}</InputLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            displayEmpty
                            IconComponent={IconDown}
                            value={type}
                            className={classes.select}
                            onChange={setType}
                        >
                            {suggestMenu}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>{'Min CVSS Score'}</InputLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            displayEmpty
                            value={score}
                            IconComponent={IconDown}
                            label={i18n('date')}
                            onChange={setScore}
                        >
                            {SCORES.map((score) => (
                                <MenuItem key={`filter-date-${score}`} value={score}>
                                    {score}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>Date</InputLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            displayEmpty
                            value={date}
                            IconComponent={IconDown}
                            label={i18n('date')}
                            onChange={setDate}
                        >
                            <MenuItem value="last 10 days">{'Last 10 Days'}</MenuItem>
                            <MenuItem value="last month">{'Last Month'}</MenuItem>
                            <MenuItem value="last 6 month">{'Last 6 Month'}</MenuItem>
                            <MenuItem value="last year">{'Last Year'}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>{'Order by'}</InputLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            displayEmpty
                            value={order}
                            IconComponent={IconDown}
                            label={i18n('orderBy')}
                            onChange={setOrder}
                        >
                            <MenuItem value="published">{'Date published'}</MenuItem>
                            <MenuItem value="cvss.score">{'CVSS Score'}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box className={classes.buttonContainer}>
                <Button className={cx(classes.button, classes.resultsButton)} onClick={handleSearch} variant="outlined">
                    {'Show Results'}
                </Button>
                <Button
                    className={cx(classes.button, classes.cancelButton)}
                    variant="outlined"
                    onClick={handleClear}
                    endIcon={<SvgIcon component={IconClose} />}
                >
                    {'Clear'}
                </Button>
            </Box>
        </Box>
    );
};

export default observer(Filter);
